import { Dispatch } from 'redux'

import { useThunkActions } from 'redux/actions/useThunkActions'

import {
  getAllAnalysisTypes,
  getAnalysisTypeRealEstates,
  updateAnalysisTypeSettings
} from 'redux/api/AnalysisTypes'

import { ApplicationState } from 'redux/Stores/types'

import type { TUpdateAnalysisTypeSettingsRequestBody } from '@homepal/api-contract'

import { SnackbarMessage } from 'types/GlobalSnackbar'

import { toggleSnackbarOpen } from '../Snackbar'

import * as actions from './actions'

export function tryGetAllAnalysisTypes() {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const { fetchStatus } = getState().AnalysisTypeStore

    if (fetchStatus === 'fetching' || fetchStatus === 'success') {
      return
    }

    dispatch(actions.getAll())

    getAllAnalysisTypes()
      .then((response) => {
        dispatch(actions.getAllSuccess(response.data.data))
      })
      .catch(() => {
        dispatch(actions.getAllFailed())
      })
  }
}

/**
 * Fetches the analysis types even if loaded, keeping the loaded ones
 * meanwhile.
 */
function tryRefetchAllAnalysisTypes() {
  return (dispatch: Dispatch) => {
    getAllAnalysisTypes()
      .then((response) => {
        dispatch(actions.getAllSuccess(response.data.data))
      })
      .catch(() => {})
  }
}

/** Fetched once, when an exclusion picker is first opened. */
function tryGetAnalysisTypeRealEstates() {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const { realEstatesFetchStatus } = getState().AnalysisTypeStore

    if (
      realEstatesFetchStatus === 'fetching' ||
      realEstatesFetchStatus === 'success'
    ) {
      return
    }

    dispatch(actions.getRealEstates())

    getAnalysisTypeRealEstates()
      .then((realEstates) => {
        dispatch(actions.getRealEstatesSuccess(realEstates))
      })
      .catch(() => {
        dispatch(actions.getRealEstatesFailed())
      })
  }
}

function tryUpdateAnalysisTypeSettings(
  analysisTypeId: string,
  body: TUpdateAnalysisTypeSettingsRequestBody,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.updateSettings())

    updateAnalysisTypeSettings(analysisTypeId, body)
      .then((response) => {
        dispatch(actions.updateSettingsSuccess(response.data.data))
        dispatch(toggleSnackbarOpen(SnackbarMessage.AnalysisTypeUpdated))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.updateSettingsFailed())
        dispatch(toggleSnackbarOpen(SnackbarMessage.AnalysisTypeUpdatedFailed))

        callback?.(true)
      })
  }
}

export const useAnalysisTypeActions = () => {
  return {
    tryGetAllAnalysisTypes: useThunkActions(tryGetAllAnalysisTypes),
    tryGetAnalysisTypeRealEstates: useThunkActions(
      tryGetAnalysisTypeRealEstates
    ),
    tryRefetchAllAnalysisTypes: useThunkActions(tryRefetchAllAnalysisTypes),
    tryUpdateAnalysisTypeSettings: useThunkActions(
      tryUpdateAnalysisTypeSettings
    )
  }
}
